import { useState, useEffect } from 'react'
import { Box, Text, HStack, VStack } from '@chakra-ui/react'
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  User,
} from 'lucide-react'
import { MenuRoot, MenuTrigger, MenuContent, MenuItem, MenuPositioner, Portal } from '@chakra-ui/react'
import type { FolderTreeNode } from '../types'
import type { CoOwnerFolderLink } from '../hooks/useCoOwnerFolders'
import { SYSTEM_FOLDER_ICONS, SYSTEM_FOLDER_COLORS } from '../config/systemFolderIcons'

/**
 * Recursively find all parent folder IDs for a given folder
 * Returns null if folder is not found in the tree
 */
function findParentFolderIds(
  folderId: string,
  tree: FolderTreeNode[],
  parentIds: Set<string> = new Set()
): Set<string> | null {
  for (const node of tree) {
    if (node.folder.id === folderId) {
      return parentIds
    }
    if (node.children.length > 0) {
      const found = findParentFolderIds(folderId, node.children, new Set([...parentIds, node.folder.id]))
      if (found !== null) {
        return found
      }
    }
  }
  return null
}

interface FolderTreeProps {
  residenceId: string
  tree: FolderTreeNode[]
  selectedFolderId?: string
  coOwnerFolders: CoOwnerFolderLink[]
  onSelectFolder: (folderId: string | null) => void
  onCreateSubfolder?: (parentFolderId: string) => void
  onEditFolder?: (folderId: string) => void
  onDeleteFolder?: (folderId: string) => void
  onDrop?: (folderId: string | null) => void
  onDropDocument?: (documentId: string, folderId: string | null) => void
}

interface FolderNodeProps {
  node: FolderTreeNode
  selectedFolderId?: string
  coOwnerFolders: CoOwnerFolderLink[]
  expandedFolderIds: Set<string>
  onToggleExpand: (folderId: string) => void
  onSelectFolder: (folderId: string | null) => void
  onCreateSubfolder?: (parentFolderId: string) => void
  onEditFolder?: (folderId: string) => void
  onDeleteFolder?: (folderId: string) => void
  onDrop?: (folderId: string | null) => void
  onDropDocument?: (documentId: string, folderId: string | null) => void
}

function FolderNode({
  node,
  selectedFolderId,
  coOwnerFolders,
  expandedFolderIds,
  onToggleExpand,
  onSelectFolder,
  onCreateSubfolder,
  onEditFolder,
  onDeleteFolder,
  onDrop,
  onDropDocument,
}: FolderNodeProps) {
  const [isDragOver, setIsDragOver] = useState(false)

  // Détection du dossier "Copropriétaires"
  const isCoOwnerFolder = node.folder.suggested_category === 'coproprietaire' || node.folder.name === 'Copropriétaires'

  // Le dossier a des enfants si :
  // - Il a des vrais enfants en DB OU
  // - C'est le dossier Copropriétaires et qu'il y a des copropriétaires
  const hasChildren = node.children.length > 0 || (isCoOwnerFolder && coOwnerFolders.length > 0)

  const isSelected = selectedFolderId === node.folder.id
  const isSystemFolder = node.folder.is_system_folder
  const isExpanded = expandedFolderIds.has(node.folder.id)

  // Get system folder icon and color if applicable
  const SystemIcon = SYSTEM_FOLDER_ICONS[node.folder.slug]
  const systemColor = SYSTEM_FOLDER_COLORS[node.folder.slug]
  const useSystemIcon = isSystemFolder && SystemIcon

  const paddingLeft = node.level * 16 + 12

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'

    if (!isDragOver) {
      setIsDragOver(true)
      console.log('📂 Drag over folder:', node.folder.name)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Only set isDragOver to false if we're actually leaving the element
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false)
      console.log('📂 Drag leave folder:', node.folder.name)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    // Check if it's a document being dropped
    const documentId = e.dataTransfer.getData('application/document-id') || e.dataTransfer.getData('text/plain')
    console.log('📥 Drop on folder:', node.folder.name, 'documentId:', documentId)

    if (documentId && onDropDocument) {
      onDropDocument(documentId, node.folder.id)
      return
    }

    // Otherwise, it's a folder being dropped
    onDrop?.(node.folder.id)
  }

  return (
    <VStack align="stretch" gap={0}>
      {/* Folder Item */}
      <HStack
        className="group"
        px={1}
        py={1.5}
        pl={`${paddingLeft}px`}
        cursor="pointer"
        bg={isDragOver ? 'blue.100' : isSelected ? 'brand.50' : 'transparent'}
        borderRadius="md"
        borderWidth={isDragOver ? '2px' : '0'}
        borderColor={isDragOver ? 'blue.500' : 'transparent'}
        _hover={{ bg: isSelected ? 'brand.100' : 'gray.50' }}
        onClick={() => {
          // Sélectionner le dossier
          onSelectFolder(node.folder.id)
          // Toggle expand/collapse si le dossier a des enfants
          if (hasChildren) {
            onToggleExpand(node.folder.id)
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        gap={2}
      >
        {/* Expand/Collapse Icon */}
        <Box
          as="button"
          w={4}
          h={4}
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={hasChildren ? 1 : 0}
          onClick={(e) => {
            e.stopPropagation()
            if (hasChildren) onToggleExpand(node.folder.id)
          }}
          flexShrink={0}
        >
          {hasChildren && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        </Box>

        {/* Folder Icon */}
        <Box
          color={useSystemIcon ? systemColor : isSelected ? 'brand.600' : 'gray.600'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          {useSystemIcon ? (
            <SystemIcon size={16} />
          ) : isExpanded && hasChildren ? (
            <FolderOpen size={16} />
          ) : (
            <Folder size={16} />
          )}
        </Box>

        {/* Folder Name */}
        <Text
          flex={1}
          fontSize="sm"
          fontWeight={isSelected ? 'semibold' : 'normal'}
          color={isSelected ? 'brand.700' : 'gray.700'}
          lineClamp={1}
        >
          {node.folder.name}
        </Text>

        {/* Document Count */}
        {node.folder.document_count !== undefined && node.folder.document_count > 0 && (
          <Text fontSize="xs" color="gray.500">
            {node.folder.document_count}
          </Text>
        )}

        {/* Actions Menu */}
        <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
          <MenuTrigger asChild>
            <Box
              as="button"
              p={1}
              borderRadius="sm"
              opacity={0}
              css={{
                '.group:hover &': {
                  opacity: 1,
                },
              }}
              _hover={{ bg: 'gray.200' }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={14} />
            </Box>
          </MenuTrigger>
          <Portal>
            <MenuPositioner>
              <MenuContent minW="160px">
                {onCreateSubfolder && (
                  <MenuItem
                    value="create-subfolder"
                    onClick={() => onCreateSubfolder(node.folder.id)}
                  >
                    <HStack gap={2}>
                      <Plus size={16} />
                      <Text>Nouveau sous-dossier</Text>
                    </HStack>
                  </MenuItem>
                )}
                {onEditFolder && !isSystemFolder && (
                  <MenuItem value="edit" onClick={() => onEditFolder(node.folder.id)}>
                    <HStack gap={2}>
                      <Pencil size={16} />
                      <Text>Renommer</Text>
                    </HStack>
                  </MenuItem>
                )}
                {onDeleteFolder && !isSystemFolder && (
                  <MenuItem value="delete" onClick={() => onDeleteFolder(node.folder.id)}>
                    <HStack gap={2}>
                      <Trash2 size={16} color="#DC2626" />
                      <Text color="red.600">Supprimer</Text>
                    </HStack>
                  </MenuItem>
                )}
              </MenuContent>
            </MenuPositioner>
          </Portal>
        </MenuRoot>
      </HStack>

      {/* Children */}
      {isExpanded && hasChildren && (
        <Box>
          {/* Regular children from DB */}
          {node.children.map((child) => (
            <FolderNode
              key={child.folder.id}
              node={child}
              selectedFolderId={selectedFolderId}
              coOwnerFolders={coOwnerFolders}
              expandedFolderIds={expandedFolderIds}
              onToggleExpand={onToggleExpand}
              onSelectFolder={onSelectFolder}
              onCreateSubfolder={onCreateSubfolder}
              onEditFolder={onEditFolder}
              onDeleteFolder={onDeleteFolder}
              onDrop={onDrop}
              onDropDocument={onDropDocument}
            />
          ))}

          {/* Virtual children: Co-owners (if this is the co-owners folder) */}
          {isCoOwnerFolder && coOwnerFolders.map((coOwner) => (
            <HStack
              key={coOwner.folder_id}
              px={1}
              py={1.5}
              pl={`${(node.level + 1) * 16 + 12}px`}
              cursor="pointer"
              bg={selectedFolderId === coOwner.linked_folder_id ? 'brand.50' : 'transparent'}
              borderRadius="md"
              _hover={{ bg: selectedFolderId === coOwner.linked_folder_id ? 'brand.100' : 'gray.50' }}
              onClick={() => coOwner.linked_folder_id && onSelectFolder(coOwner.linked_folder_id)}
              gap={2}
              opacity={coOwner.is_active ? 1 : 0.5}
            >
              {/* No chevron for leaf nodes */}
              <Box w={4} h={4} flexShrink={0} />

              {/* User Icon */}
              <Box
                color={selectedFolderId === coOwner.linked_folder_id ? 'brand.600' : coOwner.is_active ? 'blue.600' : 'gray.400'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <User size={14} />
              </Box>

              {/* Co-owner Name */}
              <Text
                flex={1}
                fontSize="sm"
                fontWeight={selectedFolderId === coOwner.linked_folder_id ? 'semibold' : 'normal'}
                color={selectedFolderId === coOwner.linked_folder_id ? 'brand.700' : coOwner.is_active ? 'gray.700' : 'gray.500'}
                lineClamp={1}
              >
                {coOwner.folder_name}
              </Text>
            </HStack>
          ))}
        </Box>
      )}
    </VStack>
  )
}

export function FolderTree({
  tree,
  selectedFolderId,
  coOwnerFolders,
  onSelectFolder,
  onCreateSubfolder,
  onEditFolder,
  onDeleteFolder,
  onDrop,
  onDropDocument,
}: FolderTreeProps) {
  const [isDragOverRoot, setIsDragOverRoot] = useState(false)
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set())

  // Auto-expand parents of selected folder
  useEffect(() => {
    if (selectedFolderId) {
      // Check if selected folder is a co-owner's physical folder
      const isCoOwnerPhysicalFolder = coOwnerFolders.some(
        (coOwner) => coOwner.linked_folder_id === selectedFolderId
      )

      if (isCoOwnerPhysicalFolder) {
        // Find the "Copropriétaires" folder and expand it
        const coOwnersFolder = tree.find(
          (node) => node.folder.suggested_category === 'coproprietaire' || node.folder.name === 'Copropriétaires'
        )
        if (coOwnersFolder) {
          setExpandedFolderIds((prev) => {
            const newSet = new Set(prev)
            newSet.add(coOwnersFolder.folder.id)
            return newSet
          })
        }
        return
      }

      const parentIds = findParentFolderIds(selectedFolderId, tree)
      if (parentIds && parentIds.size > 0) {
        setExpandedFolderIds((prev) => {
          const newSet = new Set(prev)
          parentIds.forEach((id) => newSet.add(id))
          return newSet
        })
      }
    }
  }, [selectedFolderId, tree, coOwnerFolders])

  // Toggle expand/collapse
  const handleToggleExpand = (folderId: string) => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(folderId)) {
        newSet.delete(folderId)
      } else {
        newSet.add(folderId)
      }
      return newSet
    })
  }

  const handleRootDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'

    if (!isDragOverRoot) {
      setIsDragOverRoot(true)
      console.log('📂 Drag over root')
    }
  }

  const handleRootDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOverRoot(false)
      console.log('📂 Drag leave root')
    }
  }

  const handleRootDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOverRoot(false)

    // Check if it's a document being dropped
    const documentId = e.dataTransfer.getData('application/document-id') || e.dataTransfer.getData('text/plain')
    console.log('📥 Drop on root, documentId:', documentId)

    if (documentId && onDropDocument) {
      onDropDocument(documentId, null)
      return
    }

    // Otherwise, it's a folder being dropped
    onDrop?.(null)
  }

  return (
    <VStack align="stretch" gap={1}>
      {/* Root - All Documents */}
      <HStack
        px={1}
        py={1.5}
        cursor="pointer"
        bg={isDragOverRoot ? 'blue.100' : !selectedFolderId ? 'brand.50' : 'transparent'}
        borderRadius="md"
        borderWidth={isDragOverRoot ? '2px' : '0'}
        borderColor={isDragOverRoot ? 'blue.500' : 'transparent'}
        _hover={{ bg: !selectedFolderId ? 'brand.100' : 'gray.50' }}
        onClick={() => onSelectFolder(null)}
        onDragOver={handleRootDragOver}
        onDragLeave={handleRootDragLeave}
        onDrop={handleRootDrop}
        gap={2}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          color={!selectedFolderId ? 'brand.600' : 'gray.600'}
        >
          <Folder size={16} />
        </Box>
        <Text
          fontSize="sm"
          fontWeight={!selectedFolderId ? 'semibold' : 'normal'}
          color={!selectedFolderId ? 'brand.700' : 'gray.700'}
        >
          Tous les documents
        </Text>
      </HStack>

      {/* Folder Tree */}
      {tree.map((node) => (
        <FolderNode
          key={node.folder.id}
          node={node}
          selectedFolderId={selectedFolderId}
          coOwnerFolders={coOwnerFolders}
          expandedFolderIds={expandedFolderIds}
          onToggleExpand={handleToggleExpand}
          onSelectFolder={onSelectFolder}
          onCreateSubfolder={onCreateSubfolder}
          onEditFolder={onEditFolder}
          onDeleteFolder={onDeleteFolder}
          onDrop={onDrop}
          onDropDocument={onDropDocument}
        />
      ))}
    </VStack>
  )
}
